'use client'

import { useCallback, useEffect, useRef } from 'react'

import { CHART_COLORS } from '@/features/chart/lib/constants/chart-colors'
import {
  buildExecutionMarkers,
  configureTradingPrimitive,
  createTradingPrimitiveBatch,
  fallbackAccountLabel,
  filterExecutionMarkersToVisibleRange,
  formatExecutionMarkerTooltip,
  getPositionOverlayKey,
} from '@/features/chart/lib/trading-overlay-utils'
import {
  applyPositionLineAppearance,
  calculatePnL,
  positionFromAtom,
} from '@/features/chart/lib/utils/position-utils'
import { liquidationLevelSuffix } from '@/features/dashboard/lib/position-legs'
import type { LiveTicker } from '@/features/dashboard/lib/position-live-metrics'
import type { ScaleChartLevel } from '@/features/scale/lib/scale-chart-levels'
import { useNumberFormat } from '@/hooks/use-number-format'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { toast } from '@/lib/toast'
import { type ChartDatafeed } from '@/lib/tradingview/datafeeds/chart-datafeed'
import type {
  TVOrderLine,
  TVPositionLine,
  TVWidgetInstance,
} from '@/lib/tradingview/types/tv-widget.types'
import { randomUUID } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { useUpdateOrder } from '@/service/execution.hook'
import { useRowVenueCaps } from '@/service/venue-capabilities.hook'
import { Order, OrderSide, OrderStatus, Position } from '@/types/generated/common/trading'
import { Exchange, TradeVenue } from '@/types/generated/common/types'
import { UnifiedOrderItem } from '@/types/generated/public_api'

function matchesChartPosition(position: Position, symbol: string, exchange: Exchange): boolean {
  if (position.symbol !== symbol) return false

  if (position.exchange !== Exchange.EXCHANGE_UNSPECIFIED && position.exchange !== exchange) {
    return false
  }

  const chartTradeVenue = exchangeToTradeVenue(exchange)
  if (
    chartTradeVenue !== undefined &&
    position.tradeVenue !== TradeVenue.TRADE_VENUE_UNSPECIFIED &&
    position.tradeVenue !== chartTradeVenue
  ) {
    return false
  }

  return true
}

interface UseTradingOverlaysOptions {
  widgetRef: React.RefObject<TVWidgetInstance | null>
  datafeedRef: React.RefObject<ChartDatafeed | null>
  tradesRef: React.RefObject<UnifiedOrderItem[]>
  positionsRef: React.RefObject<Position[]>
  openOrdersRef: React.RefObject<UnifiedOrderItem[]>
  scaleLevelsRef: React.RefObject<ScaleChartLevel[]>
  priceTickSize?: string
  baseCurrency?: string
  positionSizeUnit?: 'base' | 'quote'
  showPositionLine?: boolean
  showLiquidationLine?: boolean
  showBuySellTriangles?: boolean
  showBuySellButtons?: boolean
  showOpenOrders?: boolean
  getLiveTicker?: (symbol: string) => LiveTicker | undefined
  getAccountLabel?: (accountId: string) => string | undefined
  accountIds?: string[]
  exchange: Exchange
}

interface UseTradingOverlaysReturn {
  clearTradingOverlays: () => void
  loadOrderOverlays: (sym: string, tf: number) => Promise<void>
  refreshExecutionOverlays: (sym: string, tf: number) => Promise<void>
  updatePositionPnlDisplay: (sym: string) => void
}

export function useTradingOverlays({
  widgetRef,
  datafeedRef,
  tradesRef,
  positionsRef,
  openOrdersRef,
  scaleLevelsRef,
  priceTickSize,
  baseCurrency,
  positionSizeUnit = 'base',
  showPositionLine = true,
  showLiquidationLine = true,
  showBuySellTriangles = true,
  showBuySellButtons = true,
  showOpenOrders = true,
  getLiveTicker,
  getAccountLabel,
  accountIds,
  exchange,
}: UseTradingOverlaysOptions): UseTradingOverlaysReturn {
  const updateOrderMutation = useUpdateOrder()
  const rowVenueCaps = useRowVenueCaps()
  // Changes identity when hide-balances (or the separator style) toggles; the effect at the
  // bottom rebuilds every overlay's text then, since TV line labels are set imperatively.
  const fmt = useNumberFormat()
  const orderLinesRef = useRef<TVOrderLine[]>([])
  const positionLinesRef = useRef(new Map<string, TVPositionLine>())
  // Several per position when the venue reports sub-positions, each liquidated on its own.
  const liquidationLinesRef = useRef(new Map<string, TVOrderLine[]>())
  // Cancellation: each call gets an ID; stale calls clean up their own lines.
  const loadCallIdRef = useRef(0)
  // Last loaded symbol/timeframe, so a format change can rebuild the overlays. Null while
  // cleared (e.g. mid symbol change) so that rebuild never races the widget's own reload.
  const lastLoadRef = useRef<{ sym: string; tf: number } | null>(null)

  const clearLineOverlays = useCallback(() => {
    // Invalidate any in-flight load so its async results are discarded
    loadCallIdRef.current++

    for (const line of orderLinesRef.current) {
      try {
        line.remove()
      } catch {}
    }
    orderLinesRef.current = []

    for (const line of positionLinesRef.current.values()) {
      try {
        line.remove()
      } catch {}
    }
    positionLinesRef.current.clear()

    for (const line of [...liquidationLinesRef.current.values()].flat()) {
      try {
        line.remove()
      } catch {}
    }
    liquidationLinesRef.current.clear()
  }, [])

  const clearTradingOverlays = useCallback(() => {
    lastLoadRef.current = null
    clearLineOverlays()
    datafeedRef.current?.setMarksProvider()
    try {
      widgetRef.current?.activeChart().clearMarks()
    } catch {}
  }, [clearLineOverlays, datafeedRef, widgetRef])

  const snapOrderPrice = useCallback(
    (price: number) => {
      const tickNum = priceTickSize ? parseFloat(priceTickSize) : 0
      if (!tickNum || !isFinite(tickNum)) return price.toFixed(2)

      const snapped = Math.round(price / tickNum) * tickNum
      const decimals = priceTickSize?.includes('.') ? priceTickSize.split('.')[1].length : 0
      return String(parseFloat(snapped.toFixed(decimals)))
    },
    [priceTickSize]
  )

  const resolveAccountLabel = useCallback(
    (accountId: string) => getAccountLabel?.(accountId)?.trim() || fallbackAccountLabel(accountId),
    [getAccountLabel]
  )

  const formatOrderLineText = useCallback(
    (order: Order, price: string) => {
      const sideLabel = order.side === OrderSide.ORDER_SIDE_BUY ? 'BUY' : 'SELL'
      const quantity = fmt.text(order.quantity, { sensitive: true })
      return `${resolveAccountLabel(order.accountId)} · ${sideLabel} ${quantity} @ ${price}`
    },
    [resolveAccountLabel, fmt]
  )

  const isOpenPriceOrder = useCallback((order: Order, sym: string) => {
    if (order.symbol !== sym || !order.orderId || !order.accountId) return false
    if (!order.price || order.price === 'market_price') return false
    if (
      order.status === OrderStatus.ORDER_STATUS_FILLED ||
      order.status === OrderStatus.ORDER_STATUS_CANCELLED ||
      order.status === OrderStatus.ORDER_STATUS_REJECTED
    ) {
      return false
    }

    return Number.isFinite(Number(order.price)) && Number(order.price) > 0
  }, [])

  const refreshExecutionOverlays = useCallback(
    async (sym: string, _tf: number) => {
      const widget = widgetRef.current
      const datafeed = datafeedRef.current
      if (!widget || !datafeed) return

      datafeed.setMarksProvider(({ identity, from, to, timeframeSeconds, barTimes }) => {
        if (!showBuySellTriangles || identity.exchange !== exchange || identity.symbol !== sym) {
          return []
        }

        const markers = filterExecutionMarkersToVisibleRange(
          buildExecutionMarkers(tradesRef.current ?? [], {
            accountIds,
            symbol: sym,
            timeframeSeconds,
            barTimes,
          }),
          { from, to },
          timeframeSeconds
        )

        return markers.map((marker) => {
          const { bucketTime, id, isBuy } = marker
          const markerColor = isBuy ? CHART_COLORS.profit : CHART_COLORS.loss
          return {
            id,
            time: bucketTime,
            color: { border: markerColor, background: markerColor },
            text: formatExecutionMarkerTooltip(marker, fmt),
            label: isBuy ? 'B' : 'S',
            labelFontColor: CHART_COLORS.markLabel,
            minSize: 12,
            borderWidth: 0,
            hoveredBorderWidth: 1,
          }
        })
      })

      widget.activeChart().refreshMarks()
    },
    [widgetRef, datafeedRef, tradesRef, showBuySellTriangles, accountIds, exchange, fmt]
  )

  // Cheap, no-clear refresh of the position line's PnL label. Call this on every
  // mark-price/PnL tick instead of going through loadOrderOverlays — it only touches
  // text/color on the existing line, so it never causes the clear+recreate flicker.
  const updatePositionPnlDisplay = useCallback(
    (sym: string) => {
      for (const position of positionsRef.current) {
        if (!matchesChartPosition(position, sym, exchange)) continue
        const key = getPositionOverlayKey(position)
        const line = positionLinesRef.current.get(key)
        const liqLines = liquidationLinesRef.current.get(key) ?? []
        if (!line && liqLines.length === 0) continue

        const positionInfo = positionFromAtom(position, getLiveTicker?.(sym))
        if (!positionInfo) continue

        if (line) {
          const pnl = calculatePnL(positionInfo, positionInfo.markPrice ?? positionInfo.avgEntry)
          try {
            applyPositionLineAppearance(line, positionInfo, pnl, {
              baseCurrency,
              positionSizeUnit,
              accountLabel: resolveAccountLabel(position.accountId),
              fmt,
            })
          } catch {}
        }

        // Liquidation price can move (cross/portfolio margin) — keep each
        // account's line in sync without a full clear+recreate. Per-leg lines are
        // redrawn by the chart's position key instead.
        if (
          !positionInfo.liquidationLevels &&
          liqLines.length === 1 &&
          positionInfo.liquidationPrice
        ) {
          try {
            liqLines[0].setPrice(positionInfo.liquidationPrice)
          } catch {}
        }
      }
    },
    [
      positionsRef,
      getLiveTicker,
      resolveAccountLabel,
      exchange,
      fmt,
      positionSizeUnit,
      baseCurrency,
    ]
  )

  const loadOrderOverlays = useCallback(
    async (sym: string, _tf: number) => {
      const widget = widgetRef.current
      if (!widget) return
      widget.setBuySellButtonsVisible?.(showBuySellButtons)
      lastLoadRef.current = { sym, tf: _tf }

      clearLineOverlays()
      const callId = loadCallIdRef.current // snapshot after line clear (already incremented)
      await refreshExecutionOverlays(sym, _tf)

      if (loadCallIdRef.current !== callId) return

      const chart = widget.chart()
      const positions = positionsRef.current.filter((position) =>
        matchesChartPosition(position, sym, exchange)
      )

      await Promise.all(
        positions.map(async (position) => {
          const key = getPositionOverlayKey(position)
          const positionInfo = positionFromAtom(position, getLiveTicker?.(sym))
          if (!positionInfo) return
          const accountLabel = resolveAccountLabel(position.accountId)

          if (showPositionLine) {
            let line: TVPositionLine | null = null
            try {
              line = await chart.createPositionLine()
              if (loadCallIdRef.current !== callId) {
                try {
                  line.remove()
                } catch {}
                return
              }
              const pnl = calculatePnL(
                positionInfo,
                positionInfo.markPrice ?? positionInfo.avgEntry
              )
              const configuredLine = configureTradingPrimitive(line, (createdLine) => {
                applyPositionLineAppearance(createdLine, positionInfo, pnl, {
                  baseCurrency,
                  positionSizeUnit,
                  includeStaticProps: true,
                  accountLabel,
                  fmt,
                })
              })
              if (configuredLine) positionLinesRef.current.set(key, configuredLine)
            } catch {
              // Covers createPositionLine rejection and failures outside the guarded
              // appearance configuration above.
              try {
                line?.remove()
              } catch {}
              if (line && positionLinesRef.current.get(key) === line) {
                positionLinesRef.current.delete(key)
              }
            }
          }

          const legLevels = positionInfo.liquidationLevels
          const levels =
            legLevels ??
            (positionInfo.liquidationPrice
              ? [{ price: positionInfo.liquidationPrice, legCount: 1, quantity: 0 }]
              : [])
          if (showLiquidationLine && levels.length > 0) {
            try {
              const liqLines = await createTradingPrimitiveBatch(
                levels.map(() => () => chart.createOrderLine()),
                (liqLine, i) => {
                  const level = levels[i]
                  // The line nearest the mark keeps the usual look; further legs are fainter.
                  const color = i === 0 ? CHART_COLORS.loss : CHART_COLORS.lossFaint
                  const legs = legLevels
                    ? liquidationLevelSuffix(level, (qty) =>
                        fmt.number(qty, { maximumFractionDigits: 8, sensitive: true })
                      )
                    : ''
                  liqLine
                    .setPrice(level.price)
                    .setLineStyle(2)
                    .setLineWidth(1)
                    .setLineColor(color)
                    .setText(`${accountLabel} · LIQ${legs}`)
                    .setTooltip(`${accountLabel} estimated liquidation price`)
                    .setModifyTooltip('')
                    .setCancelTooltip('')
                    .setEditable(false)
                    .setCancellable(false)
                    .setBodyTextColor(color)
                    .setBodyBorderColor(color)
                    .setBodyBackgroundColor('rgba(0, 0, 0, 0.7)')
                    .setQuantity('')
                    .setQuantityBackgroundColor(CHART_COLORS.transparent)
                    .setQuantityBorderColor(CHART_COLORS.transparent)
                    .setQuantityTextColor(CHART_COLORS.transparent)
                },
                () => loadCallIdRef.current !== callId
              )
              if (!liqLines) return
              if (loadCallIdRef.current !== callId) {
                for (const line of liqLines) {
                  try {
                    line.remove()
                  } catch {}
                }
                return
              }
              liquidationLinesRef.current.set(key, liqLines)
            } catch {
              // createOrderLine not available in this library tier — skip silently
            }
          }
        })
      )

      const openOrders = showOpenOrders
        ? openOrdersRef.current
            .map((item) => item.order)
            .filter((order): order is Order => {
              if (!order) return false
              return isOpenPriceOrder(order, sym)
            })
        : []

      await Promise.all(
        openOrders.map(async (order) => {
          const originalPrice = order.price ?? ''
          const price = Number(originalPrice)
          if (!Number.isFinite(price) || price <= 0) return

          try {
            const line = await chart.createOrderLine()
            if (loadCallIdRef.current !== callId) {
              try {
                line.remove()
              } catch {}
              return
            }

            const isBuy = order.side === OrderSide.ORDER_SIDE_BUY
            const lineColor = isBuy ? CHART_COLORS.profit : CHART_COLORS.loss
            // A venue that can't amend gets a fixed line; the handlers also bail out so a
            // stray drag can never reach the venue.
            const canAmend = rowVenueCaps(order.accountId, exchange).amend
            const updateFromLine = () => {
              if (!canAmend) return
              const nextPrice = snapOrderPrice(line.getPrice())
              line.setPrice(Number(nextPrice)).setText(formatOrderLineText(order, nextPrice))

              if (nextPrice === originalPrice) return

              // One toast per order, so repeated drags of the same line replace it
              // instead of stacking.
              const toastId = `chart-order-drag-${order.orderId}`
              const label = formatSymbol(order.symbol)
              updateOrderMutation.mutate(
                {
                  accountId: order.accountId,
                  orderId: order.orderId,
                  price: nextPrice,
                  label: order.label || '',
                  symbol: order.symbol,
                  quantity: order.quantity,
                  correlationId: randomUUID(),
                },
                {
                  onSuccess: () => {
                    toast.success(`${label} order price updated to ${fmt.text(nextPrice)}`, {
                      id: toastId,
                      position: 'bottom-right',
                    })
                  },
                  onError: (error) => {
                    line.setPrice(price).setText(formatOrderLineText(order, originalPrice))
                    toast.error(error.message || `Failed to update ${label} order price`, {
                      id: toastId,
                      position: 'bottom-right',
                    })
                  },
                }
              )
            }

            line
              .setPrice(price)
              .setLineColor(lineColor)
              .setLineStyle(1)
              .setLineWidth(1)
              .setText(formatOrderLineText(order, originalPrice))
              .setTooltip(canAmend ? 'Drag to update order price' : '')
              .setModifyTooltip(canAmend ? 'Drag to update order price' : '')
              .setCancelTooltip('')
              .setQuantity(fmt.text(order.quantity, { sensitive: true }))
              .setEditable(canAmend)
              .setCancellable(false)
              .setBodyTextColor(lineColor)
              .setBodyBorderColor(lineColor)
              .setBodyBackgroundColor('rgba(0, 0, 0, 0.7)')
              .setQuantityTextColor(lineColor)
              .setQuantityBorderColor(lineColor)
              .setQuantityBackgroundColor('rgba(0, 0, 0, 0.7)')
              .onMoving(() => {
                if (!canAmend) return
                const nextPrice = snapOrderPrice(line.getPrice())
                line.setText(formatOrderLineText(order, nextPrice))
              })
              .onMove(updateFromLine)

            orderLinesRef.current.push(line)
          } catch {
            // createOrderLine not available in this library tier — skip silently
          }
        })
      )

      // A Scale's levels are owned by the algo, so they are drawn read-only: dragging one
      // would amend an order the algo tracks at its planned price.
      const scaleLevels = showOpenOrders ? scaleLevelsRef.current : []
      await Promise.all(
        scaleLevels.map(async (level) => {
          const price = Number(level.price)
          if (!Number.isFinite(price) || price <= 0) return

          try {
            const line = await chart.createOrderLine()
            if (loadCallIdRef.current !== callId) {
              try {
                line.remove()
              } catch {}
              return
            }

            const isBuy = level.side === OrderSide.ORDER_SIDE_BUY
            const lineColor = isBuy ? CHART_COLORS.profit : CHART_COLORS.loss
            const quantity = fmt.text(level.quantity, { sensitive: true })
            line
              .setPrice(price)
              .setLineColor(lineColor)
              .setLineStyle(2)
              .setLineWidth(1)
              .setText(
                `${resolveAccountLabel(level.accountId)} · Scale L${level.index + 1} · ${isBuy ? 'BUY' : 'SELL'} ${quantity} @ ${level.price}`
              )
              .setTooltip('Scale level, managed by the algo')
              .setModifyTooltip('')
              .setCancelTooltip('')
              .setQuantity(quantity)
              .setEditable(false)
              .setCancellable(false)
              .setBodyTextColor(lineColor)
              .setBodyBorderColor(lineColor)
              .setBodyBackgroundColor('rgba(0, 0, 0, 0.7)')
              .setQuantityTextColor(lineColor)
              .setQuantityBorderColor(lineColor)
              .setQuantityBackgroundColor('rgba(0, 0, 0, 0.7)')

            orderLinesRef.current.push(line)
          } catch {
            // createOrderLine not available in this library tier — skip silently
          }
        })
      )

      // Position lines and bar marks refresh together when symbol or trading state changes.
    },
    [
      widgetRef,
      positionsRef,
      openOrdersRef,
      scaleLevelsRef,
      snapOrderPrice,
      formatOrderLineText,
      isOpenPriceOrder,
      updateOrderMutation,
      clearLineOverlays,
      baseCurrency,
      positionSizeUnit,
      showPositionLine,
      showLiquidationLine,
      showBuySellButtons,
      showOpenOrders,
      getLiveTicker,
      resolveAccountLabel,
      exchange,
      rowVenueCaps,
      refreshExecutionOverlays,
      fmt,
    ]
  )

  // Rebuild position/order line text and mark tooltips when hide-balances toggles. Skips the
  // first run: the widget performs the initial load itself once the chart is ready.
  const prevFmtRef = useRef(fmt)
  useEffect(() => {
    if (prevFmtRef.current === fmt) return
    prevFmtRef.current = fmt
    const last = lastLoadRef.current
    if (last) void loadOrderOverlays(last.sym, last.tf)
  }, [fmt, loadOrderOverlays])

  return {
    clearTradingOverlays,
    loadOrderOverlays,
    refreshExecutionOverlays,
    updatePositionPnlDisplay,
  }
}
